import React from 'react';
import { connect, ResolveThunks, useDispatch } from 'react-redux';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { addWebhook, deleteWebhook, updateWebhook } from './webhookActions';
import * as Yup from 'yup';
import FormField from '../form/FormField';
import { ApiWebhook, ApiWebhookTrigger } from '../api';
import { FormikState } from 'formik/dist/types';
import { Button, SalvDropdown, SalvModal } from '@salvapp/ui-components';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { parseErrorMessage } from '../common/errors/helpers';

const validationSchema = Yup.object().shape({
  url: Yup.string().url('Valid URL required').required('Required'),
  trigger: Yup.string().required('Required'),
});

export interface WebhookModalValues {
  id?: number | undefined;
  url: string;
  token?: string;
  trigger?: ApiWebhookTrigger;
}

export interface WebhookModalProps {
  show: boolean;
  onComplete: (webhook?: ApiWebhook) => void;
  webhook: ApiWebhook | null;
}

export const WebhookModal = ({
  webhook,
  addWebhook,
  updateWebhook,
  deleteWebhook,
  show,
  onComplete,
}: Props): React.JSX.Element => {
  const dispatch = useDispatch();
  const initialValues: WebhookModalValues =
    webhook !== null
      ? {
          ...webhook,
        }
      : {
          url: '',
          token: '',
          trigger: undefined,
        };

  const initialFormikState: Partial<FormikState<WebhookModalValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const submit = async (
    values: WebhookModalValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<WebhookModalValues>
  ) => {
    try {
      let webhook: ApiWebhook;
      if (values.id) {
        webhook = await updateWebhook(values.id, values);
      } else {
        webhook = await addWebhook(values);
      }
      onComplete(webhook);
      resetForm(initialFormikState);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };
  const convertForSelect = (r: { value: any; label: string }) => {
    return {
      key: r.value,
      label: String(r.label),
      value: r.value,
    };
  };

  const triggers = Object.keys(ApiWebhookTrigger).map((key) => {
    return convertForSelect({
      label: ApiWebhookTrigger[key as keyof typeof ApiWebhookTrigger],
      value: ApiWebhookTrigger[key as keyof typeof ApiWebhookTrigger],
    });
  });

  const onDeleteWebhook = async (webhook: ApiWebhook) => {
    if (webhook.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          confirmationText: 'Yes, delete it!',
          cancelText: 'No, keep it',
          onConfirm: async () => {
            await deleteWebhook(webhook);
            onComplete(webhook);
          },
        })
      );
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={submit}
      initialValues={initialValues}
    >
      {({ isSubmitting, values, setFieldValue, resetForm }: FormikProps<WebhookModalValues>) => {
        const setTriggersValues = (field: string, value: any) => {
          setFieldValue(field, value.value);
        };

        const handleClose = () => {
          onComplete();
          resetForm();
        };

        return (
          <SalvModal show={show} onHide={handleClose} data-testid='modal'>
            <Form autoComplete='off'>
              <SalvModal.Header>{webhook ? 'Edit webhook' : 'Add webhook'} </SalvModal.Header>

              <SalvModal.Body>
                <FormField name='url' label='URL' placeholder='Enter URL' />
                <SalvDropdown
                  label={'Triggers'}
                  options={triggers}
                  value={
                    values.trigger
                      ? convertForSelect({
                          label: values.trigger,
                          value: values.trigger,
                        })
                      : undefined
                  }
                  clearable={false}
                  renderedInBootstrapModal
                  className={'m-b-16'}
                  onChange={([option]) => {
                    setTriggersValues('trigger', option);
                  }}
                />
                <FormField
                  name='token'
                  label='Authentication token (optional)'
                  placeholder='Authentication token will be sent with "X-Salv-Token" request header if set'
                />
              </SalvModal.Body>
              <SalvModal.Footer>
                <Button className='me-1' dataTestId='close' variant='secondary' onClick={handleClose}>
                  Close
                </Button>
                {webhook && (
                  <Button onClick={() => onDeleteWebhook(webhook)} variant='danger'>
                    Delete
                  </Button>
                )}
                <Button className='me-1' dataTestId='submit' disabled={isSubmitting} type='submit' variant='primary'>
                  Save
                </Button>
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

const mapDispatchToProps = {
  addWebhook,
  updateWebhook,
  deleteWebhook,
};

type Props = WebhookModalProps & ResolveThunks<typeof mapDispatchToProps>;

export default connect(null, mapDispatchToProps)(WebhookModal);
